import { signal } from '@angular/core';
import { debounce, FieldTree, max, min, required, SchemaPathTree } from '@angular/forms/signals';
import { BuriedDto, FactorsBS7671Dto } from '../../../../core/mceapi.client.generated';
import { apiOpt, apiOptSome, appOpt, getNoneOptionDto, getSomeOptionDto, isSomeOptionDto } from '../../../../core/mceapi.client.helpers';
import { match } from '../../../../shared/functions/functional';
import { AmbientTemperature, buildAmbientTemperatureSchema, createAmbientTemperature, createAmbientTemperatureDto, defaultAmbientTemperature } from '../ambient-temperature/ambient-temperature.model';
import { buildBuriedSchema, Buried, createBuried, createBuriedDto, defaultBuried } from '../buried/buried.model';
import { buildGroupingSchema, createGrouping, createGroupingDto, defaultGrouping, Grouping } from '../grouping/grouping.model';
import { buildUserDefinedFactorSchema, createUserDefinedFactor, createUserDefinedFactorDto, defaultUserDefinedFactor, UserDefinedFactor } from '../user-defined-factor/user-defined-factor.model';

export interface FactorsBS7671 {
  primaryMethodGuid: string,
  secondaryMethodGuid: string,
  ambientTemperature: AmbientTemperature;
  grouping: Grouping,
  buried: Buried,
  simultaneousOverload: boolean,
  thermalInsulationThickness: number,
  userDefinedFactor: UserDefinedFactor;
  isBuried: boolean,
  isThermallyInsulatedWall: boolean;

}

export function defaultFactorsBS7671(defaultAirTemperature: number, defaultGroundTemperature: number, defaultSoilThermalResistivtiy: number): FactorsBS7671 {
  return {
    primaryMethodGuid: '',
    secondaryMethodGuid: '',
    ambientTemperature: defaultAmbientTemperature(defaultAirTemperature, defaultGroundTemperature),
    grouping: defaultGrouping(),
    buried: defaultBuried(defaultSoilThermalResistivtiy),
    simultaneousOverload: false,
    thermalInsulationThickness: 0,
    userDefinedFactor: defaultUserDefinedFactor(),
    isBuried: false,
    isThermallyInsulatedWall: false
  };
}

export function buildFactorsBS7671Schema(path: SchemaPathTree<FactorsBS7671>) {

  required(path.thermalInsulationThickness, { message: `Thermal insulation thickness is required` });
  min(path.thermalInsulationThickness, 0, { message: `Thermal insulation thickness must be above 0 m` });
  max(path.thermalInsulationThickness, 500, { message: `Thermal insulation thickness must be below 500 mm` });
  debounce(path.thermalInsulationThickness, 500);

  buildAmbientTemperatureSchema(path.ambientTemperature);
  buildGroupingSchema(path.grouping);
  buildBuriedSchema(path.buried);
  buildUserDefinedFactorSchema(path.userDefinedFactor);
}

export function createFactorsBS7671(dto: FactorsBS7671Dto, defaultSoilThermalResistivtiy: number) {
  const primaryMethodGuid = dto.primaryMethodGuid;
  const secondaryMethodGuid = dto.secondaryMethodGuid;
  const ambientTemperature = dto.ambientTemperature;
  const grouping = dto.grouping;
  const simultaneousOverload = dto.simultaneousOverload;

  if (primaryMethodGuid === undefined
    || secondaryMethodGuid === undefined
    || ambientTemperature === undefined
    || grouping === undefined
    || simultaneousOverload === undefined) {
    throw new Error('BS 7671 factors DTO is missing required values.');
  }

  return signal<FactorsBS7671>({
    primaryMethodGuid,
    secondaryMethodGuid,
    ambientTemperature: createAmbientTemperature(ambientTemperature)(),
    grouping: createGrouping(grouping)(),
    buried:
      match(
        () => ({ depth: 0.9, soilThermalResistivity: defaultSoilThermalResistivtiy }),
        (x: BuriedDto) => createBuried(x)())
        (appOpt(dto.buried ?? {})),
    simultaneousOverload,
    thermalInsulationThickness:
      match(
        () => 0,
        (x: number) => x * 1.e3)
        (appOpt(dto.thermalInsulationThickness ?? {})),
    userDefinedFactor: createUserDefinedFactor(appOpt(dto.userDefinedFactor ?? {}))(),
    isBuried: isSomeOptionDto(dto.buried ?? {}),
    isThermallyInsulatedWall: isSomeOptionDto(dto.thermalInsulationThickness ?? {})
  });
}

export function createFactorsBS7671Dto(model: FieldTree<FactorsBS7671>) {
  return signal<FactorsBS7671Dto>({
    primaryMethodGuid: model.primaryMethodGuid().value(),
    secondaryMethodGuid: model.secondaryMethodGuid().value(),
    ambientTemperature: createAmbientTemperatureDto(model.ambientTemperature)(),
    grouping: createGroupingDto(model.grouping)(),
    buried:
      model.isBuried().value()
        ? getSomeOptionDto<BuriedDto>(createBuriedDto(model.buried)())
        : getNoneOptionDto<BuriedDto>(),
    simultaneousOverload: model.simultaneousOverload().value(),
    thermalInsulationThickness:
      model.isThermallyInsulatedWall().value()
        ? apiOptSome(model.thermalInsulationThickness().value() * 1e-3)
        : getNoneOptionDto<number>(),
    userDefinedFactor: apiOpt(createUserDefinedFactorDto(model.userDefinedFactor)())
  });
}
